import React, { Dispatch, useReducer, useRef, useState } from 'react';
import { Button, Checkbox, CircularProgress, FormControlLabel, Stack } from '@mui/material';
import { NavigateBefore as NavigateBeforeIcon } from '@mui/icons-material';
import StyledModal from '../../../../Components/GenericModal/StyledModal';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import Api from '../../../API';
import { ApplicationAction, ApplicationState, IApplication, ICustomField } from '../../../types';
import { ModalType } from '../../../config';
import { sharedClasses } from '../../../../Components/CustomUIElements/sharedClasses';
import { styles } from '../../styles';
import IntelliHRFirstStep from './IntelliHRFirstStep';
import { classes } from '../../../../Job/Applications/styles';
import { intelliHRReducer, initialState } from './intelliHRReducer';
import CreateJobStep from './CreateJobStep';
import RemunerationScheduleStep from './RemunerationScheduleStep';
import dayjs from 'dayjs';
import { scrollToElement } from '../../../../utils/scroll-to-element';
import { validateEmail } from '../../../../Candidates/EditCandidate';
import { ExitConfirmationModal } from '../../../Modals/ExitConfirmationModal';
import PersonCustomFields from './PersonCustomFields';
import { CustomFieldRenderer } from './CustomFieldRenderer';

export default function IntelliHR({
  ApplicationState,
  dispatch
}: {
  ApplicationState: ApplicationState;
  dispatch: Dispatch<ApplicationAction>;
}) {
  const { modalsOpen } = ApplicationState;
  const queryClient = useQueryClient();
  const application = queryClient.getQueryData<IApplication>(['application']);
  const [state, dispatchState] = useReducer(intelliHRReducer, {
    ...initialState,
    candidate: application?.candidate || null
  });
  const [step, setStep] = useState<number>(1);
  const [exitConfirmation, setExitConfirmation] = useState<boolean>(false);
  const [modalChecked, setModalChecked] = useState<{
    addJob: boolean;
    addRemuneration: boolean;
  }>({
    addJob: true,
    addRemuneration: true
  });

  const fieldRefs = {
    firstName: useRef<HTMLInputElement>(null),
    lastName: useRef<HTMLInputElement>(null),
    email: useRef<HTMLInputElement>(null),
    phone: useRef<HTMLInputElement>(null)
  };

  const fieldRefs2 = {
    jobStartDate: useRef<HTMLInputElement>(null),
    probationEndDate: useRef<HTMLInputElement>(null),
    positionTitle: useRef<HTMLInputElement>(null),
    businessEntity: useRef<HTMLInputElement>(null),
    businessUnit: useRef<HTMLInputElement>(null),
    location: useRef<HTMLInputElement>(null),
    supervisor: useRef<HTMLInputElement>(null),
    employmentCondition: useRef<HTMLInputElement>(null),
    payGrade: useRef<HTMLInputElement>(null),
    workClass: useRef<HTMLInputElement>(null),
    fte: useRef<HTMLInputElement>(null)
  };

  const fieldRefs3 = {
    salary: useRef<HTMLInputElement>(null),
    payCycle: useRef<HTMLInputElement>(null),
    hoursPerCycle: useRef<HTMLInputElement>(null)
  };

  useQuery({
    queryKey: ['intelliHr data'],
    queryFn: async () => {
      if (application) {
        const { res } = await Api.getIntelliHRData(application.id);
        return res;
      }
    },
    onSuccess: (data) => {
      const attachments = data?.application_assets || [];
      dispatchState({
        type: 'SET_ATTACHMENTS',
        payload: attachments.map((attachment) => ({ ...attachment, checked: true }))
      });
    },
    onError: () =>
      dispatch({
        type: 'SET_SNACKBAR',
        payload: { message: 'Error fetching intelliHR data', state: 'error' }
      })
  });

  const filterCustomFields = (
    customFieldAnswers: Record<string, { value: string | string[]; type: string }>
  ) => {
    return Object.fromEntries(
      Object.entries(customFieldAnswers).filter(([, value]) => {
        if (Array.isArray(value.value)) {
          return value.value.length !== 0;
        }
        return value.value !== '';
      })
    );
  };

  const renderCustomFields = (field: ICustomField, type: 'person' | 'job' | 'remuneration') => {
    const customFieldAnswersFn = () => {
      if (type === 'person') {
        return state.personCustomFieldAnswers;
      } else if (type === 'job') {
        return state.jobCustomFieldAnswers;
      } else {
        return state.remunerationCustomFieldAnswers;
      }
    };
    const setCustomFieldAnswerFn = () => {
      if (type === 'person') {
        return (answer: string | string[]) =>
          dispatchState({
            type: 'SET_PERSON_CUSTOM_FIELD_ANSWER',
            payload: { id: field.api_name, answer, type: field.type }
          });
      } else if (type === 'job') {
        return (answer: string | string[]) =>
          dispatchState({
            type: 'SET_JOB_CUSTOM_FIELD_ANSWER',
            payload: { id: field.api_name, answer, type: field.type }
          });
      } else {
        return (answer: string | string[]) =>
          dispatchState({
            type: 'SET_REMUNERATION_CUSTOM_FIELD_ANSWER',
            payload: { id: field.api_name, answer, type: field.type }
          });
      }
    };
    const customFieldAnswers = customFieldAnswersFn();
    const setCustomFieldAnswer = setCustomFieldAnswerFn();

    return (
      <CustomFieldRenderer
        field={field}
        customFieldAnswers={customFieldAnswers}
        setCustomFieldAnswer={setCustomFieldAnswer}
      />
    );
  };

  const probationEndDate = () => {
    if (state.probationIsNotApplicable) return undefined;
    if (state.useDefaultProbationLength) return dayjs(state.jobStartDate).add(6, 'month').toDate();
    return state.probationEndDate;
  };

  const validateInputs = () => {
    if (!state.candidate) return false;
    dispatchState({ type: 'SET_ERRORS', payload: {} });
    const inputErrors: Record<string, string> = {};
    if (step === 1) {
      if (!state.candidate?.firstname) inputErrors.firstName = 'First name is required';
      if (!state.candidate?.lastname) inputErrors.lastName = 'Last name is required';
      if (!validateEmail(state.candidate.email))
        inputErrors.email = 'Please enter a proper email address';
      if (!state.candidate.phone1.trim()) {
        inputErrors.phone = 'Phone can not be empty';
      } else if (!Number(state.candidate.phone1)) {
        inputErrors.phone = 'Please enter a proper phone number';
      }
    } else if (step === 3) {
      if (!state.jobStartDate) inputErrors.jobStartDate = 'Job start date is required';
      if (!state.probationIsNotApplicable && !state.useDefaultProbationLength) {
        if (!state.probationEndDate) {
          inputErrors.probationEndDate = 'Probation end date is required';
        } else if (
          dayjs(state.probationEndDate).isBefore(dayjs(state.jobStartDate).add(7, 'day'))
        ) {
          inputErrors.probationEndDate =
            'Probation end date must be at least 7 days from the job start date';
        }
      }
      if (!state.positionTitle) inputErrors.positionTitle = 'Position title is required';
      if (!state.businessEntity) inputErrors.businessEntity = 'Business entity is required';
      if (!state.businessUnit) inputErrors.businessUnit = 'Business unit is required';
      if (!state.location) inputErrors.location = 'Location is required';
      if (!state.noSupervisor && !state.supervisor)
        inputErrors.supervisor = 'Supervisor is required';
      if (!state.employmentCondition)
        inputErrors.employmentCondition = 'Employment condition is required';
      if (!state.payGrade) inputErrors.payGrade = 'Pay grade is required';
      if (!state.workClass) inputErrors.workClass = 'Work class is required';
      if (!state.fte && !state.noFTE) inputErrors.fte = 'FTE is required';
    } else if (step === 4) {
      if (!state.salary) inputErrors.salary = 'Salary is required';
      if (!state.payCycle) inputErrors.payCycle = 'Pay cycle is required';
      if (!state.hoursPerCycle) inputErrors.hoursPerCycle = 'Hours per cycle is required';
    }
    dispatchState({ type: 'SET_ERRORS', payload: inputErrors });
    if (inputErrors.firstName) {
      scrollToElement(fieldRefs.firstName);
    } else if (inputErrors.lastName) {
      scrollToElement(fieldRefs.lastName);
    } else if (inputErrors.email) {
      scrollToElement(fieldRefs.email);
    } else if (inputErrors.phone) {
      scrollToElement(fieldRefs.phone);
    } else if (inputErrors.jobStartDate) {
      scrollToElement(fieldRefs2.jobStartDate);
    } else if (inputErrors.probationEndDate) {
      scrollToElement(fieldRefs2.probationEndDate);
    } else if (inputErrors.positionTitle) {
      scrollToElement(fieldRefs2.positionTitle);
    } else if (inputErrors.businessEntity) {
      scrollToElement(fieldRefs2.businessEntity);
    } else if (inputErrors.businessUnit) {
      scrollToElement(fieldRefs2.businessUnit);
    } else if (inputErrors.location) {
      scrollToElement(fieldRefs2.location);
    } else if (inputErrors.supervisor) {
      scrollToElement(fieldRefs2.supervisor);
    } else if (inputErrors.employmentCondition) {
      scrollToElement(fieldRefs2.employmentCondition);
    } else if (inputErrors.payGrade) {
      scrollToElement(fieldRefs2.payGrade);
    } else if (inputErrors.workClass) {
      scrollToElement(fieldRefs2.workClass);
    } else if (inputErrors.fte) {
      scrollToElement(fieldRefs2.fte);
    } else if (inputErrors.salary) {
      scrollToElement(fieldRefs3.salary);
    } else if (inputErrors.payCycle) {
      scrollToElement(fieldRefs3.payCycle);
    } else if (inputErrors.hoursPerCycle) {
      scrollToElement(fieldRefs3.hoursPerCycle);
    }
    return !Object.keys(inputErrors).length;
  };

  const { mutate: exportToIntelliHR, isLoading: exportingIntelliHR } = useMutation({
    mutationFn: async () => {
      if (application) {
        const { res } = await Api.exportToIntelliHR(application.id, {
          title: state.title,
          firstname: state.candidate?.firstname,
          lastname: state.candidate?.lastname,
          preferred_name: state.preferredName,
          email: state.candidate?.email,
          phone: state.candidate?.phone1,
          gender: state.gender,
          date_of_birth: state.dateOfBirth,
          email_is_personal: state.personalEmail,
          phone_is_personal: state.personalPhone,
          emergency_contact_name: state.emergencyContact.name,
          emergency_contact_relationship: state.emergencyContact.relationship,
          emergency_contact_phone: state.emergencyContact.phone,
          emergency_contact_email: state.emergencyContact.email,
          custom_fields: filterCustomFields(state.personCustomFieldAnswers),
          documents: state.attachments
            .filter((attachment) => attachment.checked)
            .map((attachment) => ({
              asset_id: attachment.id,
              tag_name: attachment.file_name
            })),
          ...(step >= 3 && {
            job: {
              start_date: state.jobStartDate,
              end_date: state.hasEndDate ? state.jobEndDate : undefined,
              probation_end_date: probationEndDate(),
              position_title: state.positionTitle,
              business_entity_id: state.businessEntity?.id,
              business_unit_id: state.businessUnit?.id,
              location_id: state.location?.id,
              supervisor_person_id: state.noSupervisor ? undefined : state.supervisor?.id,
              employment_condition_id: state.employmentCondition?.id,
              pay_grade_id: state.payGrade?.id,
              work_class_id: state.workClass?.id,
              fte: state.noFTE ? undefined : state.fte,
              custom_fields: filterCustomFields(state.jobCustomFieldAnswers)
            },
            ...(step === 4 && {
              remuneration_schedule: {
                pay_type: state.remunerationScheduleType,
                currency: state.currency.code,
                pay_rate: state.salary,
                pay_cycle: state.payCycle,
                hours_per_cycle: state.hoursPerCycle,
                additions: state.additionToBase.map((addition) => ({
                  name: addition.name,
                  type: addition.type,
                  value: addition.value.value,
                  currency: state.currency.code
                })),
                additions_to_total: state.additionToTotal.map((addition) => ({
                  name: addition.name,
                  type: addition.type,
                  value: addition.value.value
                })),
                breakdowns: state.breakdowns.map((breakdown) => ({
                  name: breakdown.name,
                  type: breakdown.type,
                  value: breakdown.value.value,
                  currency: state.currency.code
                })),
                custom_fields: filterCustomFields(state.remunerationCustomFieldAnswers)
              }
            })
          })
        });
        return res;
      }
    },
    onSuccess: () => {
      dispatch({
        type: 'SET_SNACKBAR',
        payload: { message: 'Candidate successfully exported to IntelliHR', state: 'success' }
      });
      handleClose();
    },
    onError: (error: { error: string }) => {
      dispatch({ type: 'SET_SNACKBAR', payload: { message: error.error, state: 'error' } });
    }
  });

  const handleClose = () => dispatch({ type: 'SET_MODALS_OPEN', payload: null });

  const buttonName = () => {
    if (step === 1) return 'Next';
    if (step === 2) return modalChecked.addJob ? 'Next' : 'Export';
    if (step === 3) return modalChecked.addRemuneration ? 'Next' : 'Export';
    return 'Export';
  };

  const handleSubmit = () => {
    if (!validateInputs()) return;
    if (step === 1) {
      setStep(step + 1);
    } else if (step === 2) {
      modalChecked.addJob ? setStep(step + 1) : exportToIntelliHR();
    } else if (step === 3) {
      modalChecked.addRemuneration ? setStep(step + 1) : exportToIntelliHR();
    } else {
      exportToIntelliHR();
    }
  };

  const checkboxLabel = () => {
    if (step === 1) return 'Add person custom fields';
    if (step === 2) return 'Add job';
    if (step === 3) return 'Add Remuneration Schedule';
  };

  if (!state.candidate) return null;

  return (
    <StyledModal
      isOpen={modalsOpen === ModalType.INTELLIHR}
      label="IntelliHR export modal"
      handleClose={() => setExitConfirmation(true)}
      styleOverrides={{ ...styles.modalStyleOverrides, width: '900px' }}
    >
      <Stack sx={styles.modalContainer}>
        <Stack sx={styles.modalTitle}>Export to intelliHR</Stack>
        <Stack sx={{ overflow: 'auto', rowGap: 3 }}>
          {step === 1 && (
            <IntelliHRFirstStep state={state} dispatch={dispatchState} fieldRefs={fieldRefs} />
          )}
          {step === 2 && <PersonCustomFields renderCustomFields={renderCustomFields} />}
          {step === 3 && (
            <CreateJobStep
              state={state}
              dispatch={dispatchState}
              fieldRefs={fieldRefs2}
              renderCustomFields={renderCustomFields}
            />
          )}
          {step === 4 && (
            <RemunerationScheduleStep
              state={state}
              dispatch={dispatchState}
              fieldRefs={fieldRefs3}
              renderCustomFields={renderCustomFields}
            />
          )}
        </Stack>
        <Stack sx={{ flexDirection: 'row', justifyContent: 'space-between' }}>
          <Stack>
            {(step === 2 || step === 3) && (
              <FormControlLabel
                control={
                  <Checkbox
                    sx={{ ...sharedClasses.formCheckbox, padding: 'unset', marginRight: 1 }}
                    checked={step === 2 ? modalChecked.addJob : modalChecked.addRemuneration}
                    onChange={(event) =>
                      step === 2
                        ? setModalChecked({
                            ...modalChecked,
                            addJob: event.target.checked
                          })
                        : setModalChecked({
                            ...modalChecked,
                            addRemuneration: event.target.checked
                          })
                    }
                  />
                }
                label={checkboxLabel()}
                sx={{ ...sharedClasses.checkboxLabel, margin: 'unset', marginTop: '4px' }}
              />
            )}
          </Stack>
          <Stack sx={{ flexDirection: 'row', columnGap: 1 }}>
            <Button sx={classes.modalCancelButton} onClick={() => setExitConfirmation(true)}>
              Cancel
            </Button>
            {step !== 1 && (
              <Button
                onClick={() => setStep(step - 1)}
                sx={{ ...sharedClasses.genericButtonSecondary, minWidth: 'unset', padding: 1 }}
              >
                <NavigateBeforeIcon />
              </Button>
            )}
            <Button
              sx={{ ...sharedClasses.genericButton, minWidth: '95px', minHeight: '41px' }}
              onClick={handleSubmit}
            >
              {exportingIntelliHR ? <CircularProgress size={20} color="inherit" /> : buttonName()}
            </Button>
          </Stack>
        </Stack>
      </Stack>
      <ExitConfirmationModal
        exitConfirmation={exitConfirmation}
        setExitConfirmation={setExitConfirmation}
        handleClose={handleClose}
      />
    </StyledModal>
  );
}
